import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import ViewToggle, { useViewMode } from "@/components/ViewToggle";
import VersionSelector from "@/components/formula/VersionSelector";
import { Flask, CurrencyEur, Trash, Archive } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function Formulas() {
    const [items, setItems] = useState([]);
    const [bulkLoading, setBulkLoading] = useState(false);
    const [view, setView] = useViewMode("formulas", "list");

    const load = () => api.get("/formulas").then((r) => setItems(r.data));
    useEffect(() => { load(); }, []);

    const trash = async (id, e) => {
        e?.preventDefault?.();
        e?.stopPropagation?.();
        if (!window.confirm("¿Mover esta fórmula a la papelera? Podrás restaurarla después.")) return;
        try {
            await api.delete(`/formulas/${id}`);
            toast.success("Movida a la papelera");
            await load();
        } catch (err) {
            toast.error("No se pudo mover a la papelera");
        }
    };

    const runBulkPricing = async () => {
        const missing = items.filter((f) => !f.pricing && f.feasibility === "approved").length;
        if (!window.confirm(`Esto ejecutará el análisis de PVP sobre ${missing} fórmulas sin pricing. Puede tardar varios minutos y consume Claude + Brave. ¿Continuar?`)) return;
        setBulkLoading(true);
        try {
            const { data } = await api.post("/formulas/pricing/bulk");
            toast.success(`Pricing calculado en ${data.processed} fórmulas`);
            if (data.errors?.length) toast.warning(`${data.errors.length} fórmulas fallaron`);
            await load();
        } catch (e) {
            toast.error("Error en el cálculo masivo");
        } finally { setBulkLoading(false); }
    };

    const missing = items.filter((f) => !f.pricing && f.feasibility === "approved").length;

    return (
        <div className="fade-up">
            <PageHeader
                eyebrow={`Fórmulas · ${items.length}`}
                title="Diseños del laboratorio"
                lead="Borradores y fórmulas aprobadas. Cada una conserva su contexto de evidencia y composición de origen."
                actions={
                    <div className="flex items-center gap-2 flex-wrap">
                        <Link to="/formulas/papelera" className="px-3.5 py-2 border hairline bg-white text-sm hover:bg-[var(--cream)] inline-flex items-center gap-2" data-testid="formulas-trash-button">
                            <Archive size={14}/> Papelera
                        </Link>
                        {missing > 0 && (
                            <button onClick={runBulkPricing} disabled={bulkLoading} className="px-4 py-2.5 border hairline bg-white text-sm hover:bg-[var(--cream)] inline-flex items-center gap-2 disabled:opacity-50" data-testid="formulas-bulk-pricing-button">
                                <CurrencyEur size={14} weight="bold"/> {bulkLoading ? "Calculando…" : `Calcular PVP de ${missing} fórmulas`}
                            </button>
                        )}
                        <Link to="/formulas/nuevo" className="btn-primary inline-flex items-center gap-2" data-testid="formulas-new-button">
                            <Flask size={16} weight="bold" /> Nueva fórmula
                        </Link>
                    </div>
                }
            />
            <div className="px-8 md:px-12 py-8 max-w-6xl">
                <div className="flex items-center justify-end mb-4">
                    <ViewToggle value={view} onChange={setView} testid="formulas-view-toggle"/>
                </div>
                {items.length === 0 ? (
                    <div className="border hairline bg-white p-12 text-center text-[var(--ink-3)]" data-testid="formulas-empty">
                        Aún no has generado fórmulas. <Link to="/formulas/nuevo" className="text-[var(--sage)] underline">Crear la primera →</Link>
                    </div>
                ) : view === "list" ? (
                    <div className="surface overflow-x-auto" data-testid="formulas-list">
                        <table className="data-table w-full">
                            <thead><tr><th>Fórmula</th><th>Objetivo</th><th>Composición</th><th>Versión</th><th>PVP</th><th>Estado</th><th>Fecha</th><th></th></tr></thead>
                            <tbody>
                                {items.map((f) => (
                                    <tr key={f.id} data-testid={`formula-row-${f.id}`}>
                                        <td>
                                            <Link to={`/formulas/${f.id}`} className="font-medium hover:text-[var(--sage)]">{f.title}</Link>
                                            {f.status === "generating" && <span className="ml-2 text-xs text-[var(--sage)] mono animate-pulse">generando…</span>}
                                            {f.status === "error" && <span className="ml-2 text-xs text-[var(--terra-2)] mono">error</span>}
                                        </td>
                                        <td className="text-[var(--ink-2)]">{f.objective}</td>
                                        <td className="text-xs mono text-[var(--ink-2)] max-w-[260px] truncate">{(f.components || []).map((c) => `${c.compound_name} ${c.dose_mg}${c.unit||"mg"}`).join(" + ") || "—"}</td>
                                        <td><VersionSelector formula={f}/></td>
                                        <td className="mono text-xs">{f.pricing?.recommended_price ? `${f.pricing.recommended_price} ${f.pricing.currency || "€"}` : "—"}</td>
                                        <td><span className={`citation-badge ${f.status === "approved" ? "bg-[var(--sage)] text-white border-[var(--sage)]" : ""}`}>{f.status === "approved" ? "Aprobada" : f.status === "generating" ? "Generando" : f.status === "error" ? "Error" : "Borrador"}</span></td>
                                        <td className="text-xs text-[var(--ink-3)]">{new Date(f.created_at).toLocaleDateString("es-ES")}</td>
                                        <td><button onClick={(e) => trash(f.id, e)} className="text-[var(--terra-2)] hover:opacity-70" data-testid={`formula-trash-${f.id}`} title="Mover a la papelera"><Trash size={14}/></button></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <div className="grid md:grid-cols-2 gap-px bg-[var(--line)] border hairline" data-testid="formulas-cards">
                        {items.map((f) => (
                            <div key={f.id} className="bg-white relative" data-testid={`formulas-card-${f.id}`}>
                                <Link
                                    to={`/formulas/${f.id}`}
                                    className="block p-6 hover:bg-[var(--cream)] transition-colors flex flex-col gap-3"
                                >
                                    <div className="flex items-center justify-between pr-7">
                                        <span className="label-eyebrow">{f.objective}</span>
                                        <span className={`citation-badge ${f.status === "approved" ? "bg-[var(--sage)] text-white border-[var(--sage)]" : ""}`}>{f.status === "approved" ? "Aprobada" : f.status === "generating" ? "Generando" : f.status === "error" ? "Error" : "Borrador"}</span>
                                    </div>
                                    <h3 className="font-display text-xl tracking-tight leading-snug">{f.title}</h3>
                                    <div className="text-xs text-[var(--ink-2)] mono">
                                        {(f.components || []).map((c) => `${c.compound_name} ${c.dose_mg}${c.unit||"mg"}`).join(" + ") || <span className="text-[var(--ink-3)]">— sin componentes —</span>}
                                    </div>
                                    <div className="flex items-center justify-between mt-auto gap-2 min-h-[20px]">
                                        {(f.version_count || 1) > 1 ? (
                                            <span/>
                                        ) : (
                                            <div className="text-xs text-[var(--ink-3)]">
                                                {new Date(f.created_at).toLocaleDateString("es-ES")}
                                            </div>
                                        )}
                                        {f.pricing?.recommended_price && (
                                            <span className="text-xs mono text-[var(--sage)] font-medium" data-testid={`formula-pvp-${f.id}`}>{f.pricing.recommended_price} {f.pricing.currency || "€"}</span>
                                        )}
                                    </div>
                                </Link>
                                {(f.version_count || 1) > 1 && (
                                    <div className="absolute bottom-5 left-6">
                                        <VersionSelector formula={f}/>
                                    </div>
                                )}
                                <button
                                    onClick={(e) => trash(f.id, e)}
                                    className="absolute top-4 right-4 text-[var(--ink-3)] hover:text-[var(--terra-2)] p-1"
                                    data-testid={`formula-trash-${f.id}`}
                                    title="Mover a la papelera"
                                ><Trash size={14}/></button>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
